import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BudgetCard } from './budget-card';
import { BudgetTable } from './budget-table';
import { BudgetProgressBar } from './budget-progress-bar';
import { RateCardSheet } from '@/components/RateCard';
import { TableCell, TableRow } from '@/components/ui/table';
import { DataTableEnhanced, type ColumnDef, type ColumnSort } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';
import { FileText, Circle, Clock, Coins, Plus, History, Download, Edit, Receipt, CheckCircle, AlertCircle } from 'lucide-react';
import { formatCurrency, formatPercent, formatHours } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import type { SalesBudget, BillingMilestone, BillingMilestoneStatus, BillingTriggerType } from '@/types/budget.types';
import type { ProjectType } from '@/types';

// ── Invoice shape used by the Revenue Health per-milestone actuals (Phase 16.6) ──
// Intentionally minimal — matches the Invoice objects produced in project-details.tsx
// without importing from FixedFeeBillingTab (which is a separate render tree).
interface MilestoneInvoice {
  id: number;
  amount: number;
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue';
  description?: string;
  /** When present, milestone matching uses ID — avoids false positives on equal amounts */
  milestoneId?: number;
}

/** Match an invoice to a milestone — mirrors invoiceMatchesMilestone in FixedFeeBillingTab. */
function invoiceMatchesMilestone(inv: MilestoneInvoice, milestone: BillingMilestone): boolean {
  if (inv.milestoneId != null) {
    return inv.milestoneId === milestone.id;
  }
  if (Math.abs(inv.amount - milestone.amount) >= 0.01) return false;
  if (inv.description && milestone.name) {
    return inv.description.trim().toLowerCase() === milestone.name.trim().toLowerCase();
  }
  return true;
}

/**
 * Derive the display status for a milestone row — mirrors getMilestoneDisplayStatus
 * in FixedFeeBillingTab.tsx exactly. Kept in sync manually — if the Billing Tab
 * logic changes, update here too.
 */
function deriveMilestoneStatus(
  milestone: BillingMilestone,
  invoices: MilestoneInvoice[],
  completionPercent: number,
  billingTriggerType: BillingTriggerType = 'date',
): BillingMilestoneStatus {
  const forMilestone = invoices.filter((inv) =>
    invoiceMatchesMilestone(inv, milestone)
  );

  const hasInvoice = forMilestone.some((inv) => inv.status === 'Sent' || inv.status === 'Paid');

  if (hasInvoice) {
    return forMilestone.find((inv) => inv.status === 'Paid') ? 'paid' : 'due';
  }

  if (billingTriggerType === 'percent-complete') {
    const thresholdMet =
      milestone.triggerPercent !== undefined &&
      completionPercent >= milestone.triggerPercent;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlinePassed = milestone.dueDate ? new Date(milestone.dueDate) < today : false;

    if (thresholdMet && deadlinePassed) return 'overdue';
    if (!thresholdMet && deadlinePassed) return 'overdue';
    if (thresholdMet) return 'due';
    return 'pending';
  }

  return milestone.status;
}

/** Returns the invoiced amount and matched-invoice flag for a single milestone. */
function getMilestoneInvoicedAmount(
  milestone: BillingMilestone,
  invoices: MilestoneInvoice[],
): { invoicedAmount: number; hasInvoice: boolean } {
  const matched = invoices.filter((inv) =>
    invoiceMatchesMilestone(inv, milestone)
  ).filter((inv) => inv.status === 'Sent' || inv.status === 'Paid');

  const invoicedAmount = matched.reduce((sum, inv) => sum + inv.amount, 0);
  return { invoicedAmount, hasInvoice: matched.length > 0 };
}

interface SalesBudgetViewProps {
  projectId: number;
  data?: SalesBudget;
  costBudget?: any; // Cost budget data to get categories from
  projectType: ProjectType;
  /**
   * Project-level billing trigger type (Phase 7+).
   * Determines how the milestone table column is displayed ('Date' vs '% Trigger').
   * Defaults to 'date' for backwards compatibility.
   */
  billingTriggerType?: BillingTriggerType;
  /**
   * Effective completion % for this project (0–100), respecting any PM override.
   * Used by the Revenue Health panel to derive Earned to Date. (Phase 16)
   */
  completionPercent?: number;
  /**
   * Completion % calculation mode. Controls the sub-label shown under Earned to Date. (Phase 16)
   */
  completionPercentMode?: 'manual' | 'based-on-time';
  /**
   * Display name of the PM who set a manual override, if any. (Phase 16)
   */
  completionPercentOverriddenBy?: string;
  /**
   * Total already invoiced (Sent + Paid invoices) for this project. (Phase 16)
   */
  invoicedToDate?: number;
  /**
   * Individual invoice objects for this project — used to derive per-milestone
   * actuals in the Payment Schedule table. (Phase 16.6)
   */
  milestoneInvoices?: MilestoneInvoice[];
  onCreateBudget?: () => void;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
}

export function SalesBudgetView({ projectId, data, costBudget, projectType, billingTriggerType = 'date', completionPercent, completionPercentMode, completionPercentOverriddenBy, invoicedToDate, milestoneInvoices, onCreateBudget, onEdit, onExport, onHistory }: SalesBudgetViewProps) {
  // Check if data is missing or incomplete for the project type
  const isDataIncomplete = !data || 
    (projectType === 'fixed-price' && (!data.contractValue || !data.milestones || !data.revenueRecognition || !data.phases)) ||
    (projectType === 'time-and-materials' && !data.estimated && !data.estimatedRevenue && !data.categories?.length) ||
    (projectType === 'retainer' && !data.monthlyRetainer && !data.retainer);

  if (isDataIncomplete) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Coins className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Sales Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Track revenue, milestones, and billing by creating a sales budget for this project.
            </p>
            {onCreateBudget && (
              <Button onClick={onCreateBudget}>
                <Plus className="mr-2 h-4 w-4" />
                Create Sales Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (projectType === 'fixed-price') {
    return (
      <FixedPriceSalesView
        projectId={projectId}
        data={data}
        billingTriggerType={billingTriggerType}
        completionPercent={completionPercent}
        completionPercentMode={completionPercentMode}
        completionPercentOverriddenBy={completionPercentOverriddenBy}
        invoicedToDate={invoicedToDate}
        milestoneInvoices={milestoneInvoices}
        onEdit={onEdit}
        onExport={onExport}
        onHistory={onHistory}
      />
    );
  } else if (projectType === 'time-and-materials') {
    return <TimeAndMaterialsSalesView projectId={projectId} data={data} costBudget={costBudget} onEdit={onEdit} onExport={onExport} onHistory={onHistory} />;
  } else if (projectType === 'retainer') {
    return <RetainerSalesView projectId={projectId} data={data} onEdit={onEdit} onExport={onExport} onHistory={onHistory} />;
  }

  return <div>Unknown project type</div>;
}

// Fixed Price Project View
interface FixedPriceSalesViewProps {
  projectId?: number;
  data: SalesBudget;
  billingTriggerType?: BillingTriggerType;
  completionPercent?: number;
  completionPercentMode?: 'manual' | 'based-on-time';
  completionPercentOverriddenBy?: string;
  invoicedToDate?: number;
  /** Individual invoice objects — used to derive per-milestone actuals. (Phase 16.6) */
  milestoneInvoices?: MilestoneInvoice[];
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
}

function FixedPriceSalesView({
  projectId,
  data,
  billingTriggerType = 'date',
  completionPercent = 0,
  completionPercentMode = 'based-on-time',
  completionPercentOverriddenBy,
  invoicedToDate = 0,
  milestoneInvoices = [],
  onEdit,
  onExport,
  onHistory,
}: FixedPriceSalesViewProps) {
  const { contractValue = 0, milestones = [] } = data;
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  // ── Payment Schedule table sort / pagination ──
  const [scheduleColumnSorts, setScheduleColumnSorts] = useState<ColumnSort[]>([]);
  const [schedulePage, setSchedulePage] = useState(1);
  const [schedulePageSize, setSchedulePageSize] = useState(25);

  // ── Revenue Health calculations (Phase 16) ──
  const earnedToDate = contractValue > 0 ? (completionPercent / 100) * contractValue : 0;
  const wipPosition = earnedToDate - invoicedToDate;
  const wipPositionPercent = contractValue > 0 ? Math.abs(wipPosition / contractValue) * 100 : 0;
  const isOverBilled = wipPosition < 0;
  const isBalanced = !isOverBilled && wipPositionPercent <= 5;

  const earnedBarWidth = contractValue > 0 ? Math.min((earnedToDate / contractValue) * 100, 100) : 0;
  const invoicedBarWidth = contractValue > 0 ? Math.min((invoicedToDate / contractValue) * 100, 100) : 0;

  const earnedModeLabel = (() => {
    if (completionPercentOverriddenBy) return `Manual override by ${completionPercentOverriddenBy}`;
    if (completionPercentMode === 'manual') return 'Manual entry';
    return 'Auto · based on approved hours';
  })();

  const wipColour = isOverBilled
    ? 'text-error-600 dark:text-error-400'
    : isBalanced
    ? 'text-warning-600 dark:text-warning-400'
    : 'text-success-600 dark:text-success-400';

  // ── Payment Schedule — enriched rows ──
  const enrichedMilestones = useMemo(() =>
    milestones.map((m) => {
      const liveStatus = deriveMilestoneStatus(m, milestoneInvoices, completionPercent, billingTriggerType);
      const { invoicedAmount, hasInvoice } = getMilestoneInvoicedAmount(m, milestoneInvoices);
      return { ...m, liveStatus, invoicedAmount, hasInvoice, remaining: m.amount - invoicedAmount };
    }),
    [milestones, milestoneInvoices, completionPercent, billingTriggerType],
  );

  const sortedMilestones = useMemo(() => {
    if (scheduleColumnSorts.length === 0) return enrichedMilestones;
    return [...enrichedMilestones].sort((a, b) => {
      for (const sort of scheduleColumnSorts) {
        let cmp = 0;
        if (sort.columnId === 'name') cmp = a.name.localeCompare(b.name);
        else if (sort.columnId === 'trigger') cmp = (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
        else if (sort.columnId === 'amount') cmp = a.amount - b.amount;
        else if (sort.columnId === 'invoicedAmount') cmp = a.invoicedAmount - b.invoicedAmount;
        else if (sort.columnId === 'remaining') cmp = a.remaining - b.remaining;
        else if (sort.columnId === 'liveStatus') cmp = a.liveStatus.localeCompare(b.liveStatus);
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [enrichedMilestones, scheduleColumnSorts]);

  const schedulePagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedMilestones.length, schedulePage, schedulePageSize),
    [sortedMilestones.length, schedulePage, schedulePageSize],
  );
  const paginatedMilestones = useMemo(
    () => paginateArray(sortedMilestones, schedulePage, schedulePageSize).data,
    [sortedMilestones, schedulePage, schedulePageSize],
  );

  // ── Payment Schedule totals ──
  const scheduleTotalScheduled = milestones.reduce((s, m) => s + m.amount, 0);
  const scheduleTotalInvoiced = milestoneInvoices.length > 0
    ? milestones.reduce((s, m) => s + getMilestoneInvoicedAmount(m, milestoneInvoices).invoicedAmount, 0)
    : null;
  const scheduleTotalRemaining = scheduleTotalInvoiced !== null
    ? milestones.reduce((s, m) => s + (m.amount - getMilestoneInvoicedAmount(m, milestoneInvoices).invoicedAmount), 0)
    : null;

  // ── Payment Schedule column defs ──
  const scheduleColumns: ColumnDef<typeof enrichedMilestones[number], unknown>[] = [
    { id: 'name', accessorKey: 'name', header: 'Milestone', enableSorting: true },
    { id: 'trigger', accessorKey: 'dueDate', header: billingTriggerType === 'percent-complete' ? 'Trigger' : 'Due Date', enableSorting: true },
    { id: 'amount', accessorKey: 'amount', header: 'Scheduled', enableSorting: true, meta: { align: 'right' } },
    { id: 'invoicedAmount', accessorKey: 'invoicedAmount', header: 'Invoiced', enableSorting: true, meta: { align: 'right' } },
    { id: 'remaining', accessorKey: 'remaining', header: 'Remaining', enableSorting: true, meta: { align: 'right' } },
    { id: 'liveStatus', accessorKey: 'liveStatus', header: 'Status', enableSorting: true, meta: { align: 'right' } },
  ];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
      <CardContent className="space-y-6">
        {/* One-line Summary */}
        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-sm text-neutral-600">Contract Value:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(contractValue)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Scheduled:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(milestones.reduce((sum, m) => sum + m.amount, 0))}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Milestones:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {milestones.length}
              </span>
            </div>
          </div>
          <div>
            <Badge variant="outline">
              Billed by: {billingTriggerType === 'percent-complete' ? '% Complete' : 'Date'}
            </Badge>
          </div>
        </div>

        {/* ── Revenue Health Panel (Phase 16) ── */}
        {contractValue > 0 ? (
          <div className="rounded-lg border border-neutral-200 bg-white dark:bg-neutral-950 p-5 space-y-5">
            <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Revenue Health</h3>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* 1 — Contract Value */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 dark:bg-neutral-900 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Contract Value</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatCurrency(contractValue)}</p>
              </div>

              {/* 2 — Earned to Date */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 dark:bg-neutral-900 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Earned to Date</p>
                <p className={cn("text-xl font-bold", earnedToDate > 0 ? 'text-success-600 dark:text-success-400' : 'text-neutral-900 dark:text-neutral-100')}>
                  {formatCurrency(earnedToDate)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{completionPercent}% complete</p>
                <p className="text-xs text-muted-foreground mt-0.5 italic">{earnedModeLabel}</p>
              </div>

              {/* 3 — Invoiced to Date */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 dark:bg-neutral-900 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Invoiced to Date</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatCurrency(invoicedToDate)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {contractValue > 0 ? formatPercent((invoicedToDate / contractValue) * 100, 1) : '0%'} of contract
                </p>
              </div>

              {/* 4 — WIP Position */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 dark:bg-neutral-900 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">WIP Position</p>
                <p className={cn("text-xl font-bold", wipColour)}>
                  {wipPosition >= 0 ? '+' : ''}{formatCurrency(wipPosition)}
                </p>
                <div className="mt-1">
                  {isOverBilled ? (
                    <Badge variant="destructive" className="text-xs">Over-billed</Badge>
                  ) : isBalanced ? (
                    <Badge variant="warning" className="text-xs">Balanced</Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground">Under-billed</p>
                  )}
                </div>
              </div>
            </div>

            {/* Dual-fill Progress Bar */}
            <div className="space-y-2">
              <div className="relative h-4 w-full rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                {/* Earned fill — primary */}
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${earnedBarWidth}%` }}
                />
                {/* Invoiced fill — secondary/muted, layered on top */}
                <div
                  className={cn(
                    "absolute left-0 top-0 h-full rounded-full transition-all duration-500",
                    isOverBilled ? "bg-error-500 dark:bg-error-600" : "bg-neutral-400 dark:bg-neutral-500"
                  )}
                  style={{ width: `${invoicedBarWidth}%`, opacity: 0.6 }}
                />
              </div>
              {/* Legend */}
              <div className="flex items-center gap-5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary" />
                  <span>Earned ({formatPercent(earnedBarWidth, 0)})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={cn("inline-block h-2.5 w-2.5 rounded-full", isOverBilled ? "bg-error-500" : "bg-neutral-400")} />
                  <span>Invoiced ({formatPercent(invoicedBarWidth, 0)})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700 border border-neutral-300" />
                  <span>Remaining ({formatPercent(Math.max(0, 100 - invoicedBarWidth), 0)})</span>
                </div>
              </div>
            </div>

            {/* Over-billing advisory alert */}
            {isOverBilled && (
              <Alert variant="warning">
                <AlertDescription>
                  This project has been invoiced more than its earned value. Review the billing schedule and completion % before raising further invoices.
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 dark:bg-neutral-900 p-4 text-sm text-muted-foreground text-center">
            Set a contract value to see revenue tracking.
          </div>
        )}

        {/* Payment Schedule Table */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Payment Schedule</h3>
            {milestoneInvoices.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Invoiced amounts sourced from invoice records
              </p>
            )}
          </div>
          <DataTableEnhanced
            columns={scheduleColumns}
            data={paginatedMilestones}
            columnSorts={scheduleColumnSorts}
            onColumnSortChange={(newSorts) => {
              setScheduleColumnSorts(newSorts);
              setSchedulePage(1);
            }}
            emptyMessage={<span className="text-muted-foreground text-sm">No milestones defined</span>}
            customRowRenderer={(row) => {
              const milestone = row.original;
              const isPercentComplete = billingTriggerType === 'percent-complete';
              let statusLabel: string;
              let statusVariant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive';
              let StatusIcon: React.ElementType | null = null;
              if (milestone.liveStatus === 'paid') { statusLabel = 'Paid'; statusVariant = 'success'; StatusIcon = CheckCircle; }
              else if (milestone.liveStatus === 'due' && milestone.hasInvoice) { statusLabel = 'Invoiced'; statusVariant = 'default'; StatusIcon = FileText; }
              else if (milestone.liveStatus === 'due') { statusLabel = 'Due'; statusVariant = 'warning'; StatusIcon = Clock; }
              else if (milestone.liveStatus === 'overdue') { statusLabel = 'Overdue'; statusVariant = 'destructive'; StatusIcon = AlertCircle; }
              else { statusLabel = 'Scheduled'; statusVariant = 'secondary'; StatusIcon = Circle; }
              return (
                <TableRow key={milestone.id} className="odd:bg-white even:bg-neutral-50 hover:bg-neutral-100">
                  <TableCell className="font-medium">{milestone.name}</TableCell>
                  <TableCell>
                    {isPercentComplete ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className={cn('inline-block h-2 w-2 rounded-full flex-shrink-0', milestone.liveStatus === 'due' || milestone.liveStatus === 'overdue' || milestone.liveStatus === 'paid' ? 'bg-success-500' : 'bg-neutral-300')} />
                          <span className="text-sm font-medium">at {milestone.triggerPercent}%</span>
                        </div>
                        {milestone.dueDate && (
                          <span className="text-xs text-muted-foreground">Deadline: {new Date(milestone.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm">
                        {milestone.dueDate ? new Date(milestone.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : <span className="text-muted-foreground">—</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(milestone.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {milestone.invoicedAmount > 0 ? <span className="font-medium">{formatCurrency(milestone.invoicedAmount)}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {milestone.remaining === 0 ? <span className="text-muted-foreground">—</span> : <span className={cn('font-medium', milestone.remaining < 0 ? 'text-error-600 dark:text-error-400' : 'text-neutral-900 dark:text-neutral-100')}>{formatCurrency(milestone.remaining)}</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={statusVariant} className="gap-1 whitespace-nowrap">
                      {StatusIcon && <StatusIcon className="h-3 w-3" />}
                      {statusLabel}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            }}
            footerContent={
              <TableRow className="font-bold bg-muted/50 border-t-2">
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(scheduleTotalScheduled)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {scheduleTotalInvoiced !== null ? formatCurrency(scheduleTotalInvoiced) : <span className="text-muted-foreground font-normal">—</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {scheduleTotalRemaining !== null ? formatCurrency(scheduleTotalRemaining) : <span className="text-muted-foreground font-normal">—</span>}
                </TableCell>
                <TableCell />
              </TableRow>
            }
            paginationContent={
              <PaginationControls
                pagination={schedulePagination}
                onPageChange={setSchedulePage}
                onPageSizeChange={(size) => { setSchedulePageSize(size); setSchedulePage(1); }}
              />
            }
          />
        </div>
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}

// Time & Materials Project View
function TimeAndMaterialsSalesView({ projectId, data, costBudget, onEdit, onExport, onHistory }: { projectId?: number; data: SalesBudget; costBudget?: any; onEdit?: () => void; onExport?: () => void; onHistory?: () => void }) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);
  const [catColumnSorts, setCatColumnSorts] = useState<ColumnSort[]>([]);
  const [catPage, setCatPage] = useState(1);
  const [catPageSize, setCatPageSize] = useState(25);

  // Use categories from API data if available, otherwise fall back to defaults
  // Deduplicate by name, keeping the first occurrence of each category
  const categories = React.useMemo(() => {
    if (data.categories && data.categories.length > 0) {
      const seen = new Set<string>();
      return data.categories
        .map(cat => ({
          id: String(cat.id),
          name: cat.name,
          budget: cat.budget,
          actual: cat.actual,
        }))
        .filter(cat => {
          if (seen.has(cat.name)) return false;
          seen.add(cat.name);
          return true;
        });
    }
    // Fallback default categories for display when no API data exists
    return [
      { id: '1', name: 'Labour', budget: 350000, actual: 231000 },
      { id: '2', name: 'Materials', budget: 104000, actual: 0 },
      { id: '3', name: 'Equipment', budget: 78000, actual: 49400 },
      { id: '4', name: 'Subcontractors', budget: 84000, actual: 0 },
      { id: '5', name: 'Travel & Expenses', budget: 0, actual: 0 },
      { id: '6', name: 'Software & Licenses', budget: 36400, actual: 23400 },
      { id: '7', name: 'Overhead', budget: 0, actual: 0 },
      { id: '8', name: 'Contingency', budget: 19500, actual: 11700 },
    ];
  }, [data.categories]);

  // Calculate totals from categories
  const budget = categories.reduce((sum: number, cat: { budget: number; actual: number }) => sum + cat.budget, 0);
  const actual = categories.reduce((sum: number, cat: { budget: number; actual: number }) => sum + cat.actual, 0);
  const variance = budget - actual; // Budget minus Actual = remaining budget (positive is good)
  const utilizationPercent = budget > 0 ? (actual / budget) * 100 : 0;
  const NEAR_BUDGET_THRESHOLD = 80; // 80% utilization triggers warning

  // ── Category table sort / pagination ──
  const enrichedCategories = useMemo(() =>
    categories.map((cat) => {
      const remaining = cat.budget - cat.actual;
      const percentBilled = cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0;
      return { ...cat, remaining, percentBilled };
    }),
    [categories],
  );

  const sortedCategories = useMemo(() => {
    if (catColumnSorts.length === 0) return enrichedCategories;
    return [...enrichedCategories].sort((a, b) => {
      for (const sort of catColumnSorts) {
        let cmp = 0;
        if (sort.columnId === 'name') cmp = a.name.localeCompare(b.name);
        else if (sort.columnId === 'budget') cmp = a.budget - b.budget;
        else if (sort.columnId === 'actual') cmp = a.actual - b.actual;
        else if (sort.columnId === 'remaining') cmp = a.remaining - b.remaining;
        else if (sort.columnId === 'percentBilled') cmp = a.percentBilled - b.percentBilled;
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [enrichedCategories, catColumnSorts]);

  const catPagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedCategories.length, catPage, catPageSize),
    [sortedCategories.length, catPage, catPageSize],
  );
  const paginatedCategories = useMemo(
    () => paginateArray(sortedCategories, catPage, catPageSize).data,
    [sortedCategories, catPage, catPageSize],
  );

  const catColumns: ColumnDef<typeof enrichedCategories[number], unknown>[] = [
    { id: 'name', accessorKey: 'name', header: 'Category', enableSorting: true },
    { id: 'budget', accessorKey: 'budget', header: 'Budget', enableSorting: true, meta: { align: 'right' } },
    { id: 'actual', accessorKey: 'actual', header: 'Actual', enableSorting: true, meta: { align: 'right' } },
    { id: 'remaining', accessorKey: 'remaining', header: 'Remaining', enableSorting: true, meta: { align: 'right' } },
    { id: 'percentBilled', accessorKey: 'percentBilled', header: '% Billed', enableSorting: true, meta: { align: 'right' } },
    { id: 'status', accessorKey: 'percentBilled', header: 'Status', enableSorting: false, meta: { align: 'right' } },
  ];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
      <CardContent className="space-y-6">
        {/* One-line Summary */}
        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-sm text-neutral-600">Budget:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(budget)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Actual:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(actual)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Remaining:</span>
              <span className={cn(
                "ml-2 text-base font-semibold",
                variance < 0 
                  ? "text-error-600" // Over budget - red
                  : utilizationPercent >= NEAR_BUDGET_THRESHOLD 
                    ? "text-warning-600" // Nearing budget (80%+) - amber
                    : variance > 0 
                      ? "text-success-600" // Under budget - green
                      : "text-neutral-900" // Exactly on budget
              )}>
                {variance > 0 ? '+' : ''}{formatCurrency(variance)}
              </span>
            </div>
          </div>
        </div>

        {/* Budget Categories Table */}
        <DataTableEnhanced
          columns={catColumns}
          data={paginatedCategories}
          columnSorts={catColumnSorts}
          onColumnSortChange={(newSorts) => {
            setCatColumnSorts(newSorts);
            setCatPage(1);
          }}
          emptyMessage={<span className="text-muted-foreground text-sm">No categories defined</span>}
          customRowRenderer={(row) => {
            const category = row.original;
            let status: 'success' | 'warning' | 'destructive' = 'success';
            let statusText = 'On Track';
            if (category.percentBilled >= 100) { status = 'destructive'; statusText = 'Over Budget'; }
            else if (category.percentBilled >= 80) { status = 'warning'; statusText = 'At Risk'; }
            return (
              <TableRow key={category.id} className="odd:bg-white even:bg-neutral-50 hover:bg-neutral-100">
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(category.budget)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(category.actual)}</TableCell>
                <TableCell className={cn('text-right tabular-nums font-medium', category.remaining >= 0 ? 'text-success-600' : 'text-error-600')}>
                  {category.remaining >= 0 ? '+' : ''}{formatCurrency(category.remaining)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatPercent(category.percentBilled, 1)}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={status} accent={status}>{statusText}</Badge>
                </TableCell>
              </TableRow>
            );
          }}
          footerContent={
            <TableRow className="font-bold bg-muted/50 border-t-2">
              <TableCell>Total</TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(budget)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(actual)}</TableCell>
              <TableCell className={cn('text-right tabular-nums', variance < 0 ? 'text-error-600' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'text-warning-600' : variance > 0 ? 'text-success-600' : 'text-neutral-900')}>
                {variance > 0 ? '+' : ''}{formatCurrency(variance)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatPercent(utilizationPercent, 1)}</TableCell>
              <TableCell className="text-right">
                <Badge variant={variance < 0 ? 'destructive' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'warning' : 'success'} accent={variance < 0 ? 'destructive' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'warning' : 'success'}>
                  {variance < 0 ? 'Over Budget' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'At Risk' : 'On Track'}
                </Badge>
              </TableCell>
            </TableRow>
          }
          paginationContent={
            <PaginationControls
              pagination={catPagination}
              onPageChange={setCatPage}
              onPageSizeChange={(size) => { setCatPageSize(size); setCatPage(1); }}
            />
          }
        />
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}

// Retainer Project View
function RetainerSalesView({ projectId, data, onEdit, onExport, onHistory }: { projectId?: number; data: SalesBudget; onEdit?: () => void; onExport?: () => void; onHistory?: () => void }) {
  const { retainer, utilisation, monthlyBreakdown = [] } = data;
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  const monthlyRows = monthlyBreakdown.map((month, index) => ({
    id: index + 1,
    month: month.month,
    retainer: formatCurrency(month.retainerAmount),
    hoursIncluded: formatHours(month.hoursIncluded),
    hoursUsed: formatHours(month.hoursUsed),
    overage: month.overageAmount > 0 ? formatCurrency(month.overageAmount) : '-',
    total: formatCurrency(month.totalBilled),
  }));

  // Calculate YTD totals
  const ytdTotals = monthlyBreakdown.reduce(
    (acc, month) => ({
      retainer: acc.retainer + month.retainerAmount,
      hoursIncluded: acc.hoursIncluded + month.hoursIncluded,
      hoursUsed: acc.hoursUsed + month.hoursUsed,
      overage: acc.overage + month.overageAmount,
      total: acc.total + month.totalBilled,
    }),
    { retainer: 0, hoursIncluded: 0, hoursUsed: 0, overage: 0, total: 0 }
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
        <div className="space-y-6">
          {/* Retainer Summary */}
          {retainer && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Monthly Retainer"
          value={formatCurrency(retainer.monthlyAmount)}
          subtitle="/month"
          variant="default"
        />
        <BudgetCard
          title="Contract Value"
          value={formatCurrency(retainer.contractValue)}
          subtitle={`${retainer.contractMonths} months`}
          variant="default"
        />
        <BudgetCard
          title="Invoiced"
          value={formatCurrency(retainer.invoiced)}
          subtitle={`${retainer.monthsCompleted} months`}
          variant="default"
        />
        <BudgetCard
          title="Remaining"
          value={formatCurrency(retainer.remaining)}
          subtitle={`${retainer.monthsRemaining} months`}
          variant="default"
        />
      </div>
          )}

      {/* Hours Utilisation - Current Month */}
      {utilisation && (
      <Card>
        <CardHeader>
          <CardTitle>Hours Utilisation (Current Month: {utilisation.currentMonth})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Included Hours:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.includedHours)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Used:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursUsed)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Remaining:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursRemaining)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Overage Rate:</div>
                <div className="text-2xl font-bold">{formatCurrency(utilisation.overageRate)}/hr</div>
              </div>
            </div>

            <BudgetProgressBar
              value={utilisation.percentUsed}
              max={100}
              variant="default"
              showLabel
              label={`${formatPercent(utilisation.percentUsed)} used`}
            />

            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">Rollover Policy:</span>
              <div className="flex gap-2">
                <Badge variant={utilisation.rolloverPolicy === 'none' ? 'default' : 'outline'}>
                  No rollover
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'limited' ? 'default' : 'outline'}>
                  Max {utilisation.rolloverHours} hrs
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'unlimited' ? 'default' : 'outline'}>
                  Unlimited
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'month', label: 'Month', align: 'left' },
              { key: 'retainer', label: 'Retainer', align: 'right' },
              { key: 'hoursIncluded', label: 'Hours Incl', align: 'right' },
              { key: 'hoursUsed', label: 'Hours Used', align: 'right' },
              { key: 'overage', label: 'Overage', align: 'right' },
              { key: 'total', label: 'Total', align: 'right' },
            ]}
            data={monthlyRows}
            totalRow={{
              month: 'YTD Total',
              retainer: formatCurrency(ytdTotals.retainer),
              hoursIncluded: formatHours(ytdTotals.hoursIncluded),
              hoursUsed: formatHours(ytdTotals.hoursUsed),
              overage: formatCurrency(ytdTotals.overage),
              total: formatCurrency(ytdTotals.total),
            }}
          />
        </CardContent>
      </Card>
        </div>
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}
